import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  getDocs,
  doc,
  updateDoc,
  limit,
  getDoc
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase';

/**
 * 채팅 데이터 구조:
 *
 * chatRooms (컬렉션)
 * - id: 채팅방 ID (자동 생성)
 * - teacherId: 치료사 ID
 * - momId: 학부모 ID
 * - studentName: 학생 이름 (편의를 위해)
 * - lastMessage: 마지막 메시지 내용
 * - lastMessageAt: 마지막 메시지 시간
 * - unreadCount: { teacherId: number, momId: number } - 각 사용자별 읽지 않은 메시지 수
 * - createdAt: 채팅방 생성 시간
 * - updatedAt: 마지막 업데이트 시간
 *
 * messages (컬렉션)
 * - id: 메시지 ID (자동 생성)
 * - chatRoomId: 채팅방 ID
 * - senderId: 발신자 ID
 * - senderType: 'teacher' | 'mom'
 * - senderName: 발신자 이름
 * - message: 메시지 내용
 * - mediaType: 'text' | 'image' | 'video' - 미디어 타입
 * - mediaUrl: 미디어 파일 URL (이미지/동영상인 경우)
 * - thumbnailUrl: 동영상 썸네일 URL (동영상인 경우)
 * - fileName: 원본 파일 이름
 * - fileSize: 파일 크기 (bytes)
 * - createdAt: 메시지 전송 시간
 * - isRead: 읽음 여부
 */

export const chatService = {
  /**
   * 채팅방 찾기 또는 생성
   */
  findOrCreateChatRoom: async (teacherId, momId, studentName) => {
    try {
      // 기존 채팅방 찾기
      const chatRoomsRef = collection(db, 'chatRooms');
      const q = query(
        chatRoomsRef,
        where('teacherId', '==', teacherId),
        where('momId', '==', momId)
      );

      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        // 기존 채팅방 반환
        const chatRoom = querySnapshot.docs[0];
        return {
          id: chatRoom.id,
          ...chatRoom.data()
        };
      }

      // 새 채팅방 생성
      const newChatRoom = {
        teacherId,
        momId,
        studentName,
        lastMessage: '',
        lastMessageAt: serverTimestamp(),
        unreadCount: {
          [teacherId]: 0,
          [momId]: 0
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(chatRoomsRef, newChatRoom);

      return {
        id: docRef.id,
        ...newChatRoom
      };
    } catch (error) {
      console.error('Error finding or creating chat room:', error);
      throw error;
    }
  },

  /**
   * 사용자의 채팅방 목록 가져오기
   */
  getUserChatRooms: async (userId, userType) => {
    try {
      const chatRoomsRef = collection(db, 'chatRooms');
      const field = userType === 'teacher' ? 'teacherId' : 'momId';
      const q = query(
        chatRoomsRef,
        where(field, '==', userId),
        orderBy('lastMessageAt', 'desc')
      );

      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting user chat rooms:', error);
      throw error;
    }
  },

  /**
   * 채팅방 실시간 구독
   */
  subscribeToChatRooms: (userId, userType, callback) => {
    const chatRoomsRef = collection(db, 'chatRooms');
    const field = userType === 'teacher' ? 'teacherId' : 'momId';
    const q = query(
      chatRoomsRef,
      where(field, '==', userId),
      orderBy('lastMessageAt', 'desc')
    );

    return onSnapshot(q, (snapshot) => {
      const chatRooms = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      callback(chatRooms);
    });
  },

  /**
   * 메시지 전송
   * @param {object} messageData - 메시지 데이터
   * @param {string} messageData.chatRoomId - 채팅방 ID
   * @param {string} messageData.senderId - 발신자 ID
   * @param {string} messageData.senderType - 발신자 타입 ('teacher' | 'mom')
   * @param {string} messageData.senderName - 발신자 이름
   * @param {string} messageData.message - 메시지 내용
   * @param {string} messageData.mediaType - 미디어 타입 ('text' | 'image' | 'video')
   * @param {string} messageData.mediaUrl - 미디어 URL (선택)
   * @param {string} messageData.thumbnailUrl - 썸네일 URL (선택)
   * @param {string} messageData.fileName - 파일 이름 (선택)
   * @param {number} messageData.fileSize - 파일 크기 (선택)
   */
  sendMessage: async (messageData) => {
    try {
      const {
        chatRoomId,
        senderId,
        senderType,
        senderName,
        message,
        mediaType = 'text',
        mediaUrl = null,
        thumbnailUrl = null,
        fileName = null,
        fileSize = null
      } = messageData;

      // 메시지 추가
      const messagesRef = collection(db, 'messages');
      const newMessage = {
        chatRoomId,
        senderId,
        senderType,
        senderName,
        message,
        mediaType,
        mediaUrl,
        thumbnailUrl,
        fileName,
        fileSize,
        createdAt: serverTimestamp(),
        isRead: false
      };

      await addDoc(messagesRef, newMessage);

      // 채팅방 정보 업데이트
      const chatRoomRef = doc(db, 'chatRooms', chatRoomId);
      const chatRoomDoc = await getDoc(chatRoomRef);

      if (chatRoomDoc.exists()) {
        const chatRoomData = chatRoomDoc.data();
        const otherUserId = senderType === 'teacher'
          ? chatRoomData.momId
          : chatRoomData.teacherId;

        // lastMessage는 미디어 타입에 따라 다르게 설정
        let lastMessage = message;
        if (mediaType === 'image') {
          lastMessage = '📷 사진';
        } else if (mediaType === 'video') {
          lastMessage = '🎥 동영상';
        }

        await updateDoc(chatRoomRef, {
          lastMessage,
          lastMessageAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          [`unreadCount.${otherUserId}`]: (chatRoomData.unreadCount?.[otherUserId] || 0) + 1
        });
      }

      return newMessage;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  },

  /**
   * 메시지 목록 실시간 구독
   */
  subscribeToMessages: (chatRoomId, callback) => {
    const messagesRef = collection(db, 'messages');
    const q = query(
      messagesRef,
      where('chatRoomId', '==', chatRoomId),
      orderBy('createdAt', 'asc')
    );

    return onSnapshot(q, (snapshot) => {
      const messages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      callback(messages);
    });
  },

  /**
   * 메시지 읽음 처리
   */
  markMessagesAsRead: async (chatRoomId, userId) => {
    try {
      // 채팅방의 읽지 않은 메시지 수 초기화
      const chatRoomRef = doc(db, 'chatRooms', chatRoomId);
      await updateDoc(chatRoomRef, {
        [`unreadCount.${userId}`]: 0
      });

      // 메시지들을 읽음으로 표시
      const messagesRef = collection(db, 'messages');
      const q = query(
        messagesRef,
        where('chatRoomId', '==', chatRoomId),
        where('isRead', '==', false)
      );

      const querySnapshot = await getDocs(q);

      const updatePromises = querySnapshot.docs
        .filter(doc => doc.data().senderId !== userId)
        .map(doc => updateDoc(doc.ref, { isRead: true }));

      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error marking messages as read:', error);
      throw error;
    }
  },

  /**
   * 전체 읽지 않은 메시지 수 가져오기
   */
  getTotalUnreadCount: async (userId, userType) => {
    try {
      const chatRooms = await chatService.getUserChatRooms(userId, userType);
      return chatRooms.reduce((total, room) => {
        return total + (room.unreadCount?.[userId] || 0);
      }, 0);
    } catch (error) {
      console.error('Error getting total unread count:', error);
      return 0;
    }
  },

  /**
   * 읽지 않은 메시지 수 실시간 구독
   */
  subscribeToUnreadCount: (userId, userType, callback) => {
    return chatService.subscribeToChatRooms(userId, userType, (chatRooms) => {
      const totalUnread = chatRooms.reduce((total, room) => {
        return total + (room.unreadCount?.[userId] || 0);
      }, 0);
      callback(totalUnread);
    });
  },

  /**
   * Firebase Storage에 파일 업로드
   * @param {File} file - 업로드할 파일
   * @param {string} chatRoomId - 채팅방 ID
   * @param {function} onProgress - 업로드 진행률 콜백 (선택)
   * @returns {Promise<{url: string, fileName: string, fileSize: number}>}
   */
  uploadFile: async (file, chatRoomId, onProgress = null) => {
    try {
      if (!file) {
        throw new Error('파일이 선택되지 않았습니다.');
      }

      // 파일 크기 제한 (10MB)
      const MAX_FILE_SIZE = 10 * 1024 * 1024;
      if (file.size > MAX_FILE_SIZE) {
        throw new Error('파일 크기는 10MB를 초과할 수 없습니다.');
      }

      // 파일 타입 확인
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/quicktime'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('지원하지 않는 파일 형식입니다. (이미지: JPG, PNG, GIF, WEBP / 동영상: MP4, MOV)');
      }

      // 파일 경로 생성: chat-media/{chatRoomId}/{timestamp}_{filename}
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const storageRef = ref(storage, `chat-media/${chatRoomId}/${fileName}`);

      // 업로드 시작
      const uploadTask = uploadBytesResumable(storageRef, file);

      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            // 업로드 진행률 계산
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            if (onProgress) {
              onProgress(progress);
            }
            console.log(`Upload is ${progress}% done`);
          },
          (error) => {
            // 업로드 실패
            console.error('Upload error:', error);
            reject(error);
          },
          async () => {
            // 업로드 성공
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              resolve({
                url: downloadURL,
                fileName: file.name,
                fileSize: file.size
              });
            } catch (error) {
              reject(error);
            }
          }
        );
      });
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  }
};
